import { createContext, useEffect, useState } from "react";
import type { FC } from "react";
import {
  useGetNotificationBadgeCountQuery,
  useReadNotificationMutation,
  useResetNotificationBadgeCountMutation,
} from "src/service/NotificationUserApi/NotificationUserApi";

export type NotificationUserState = {
  badgeCount: number;
  hasNew: boolean;
  onResetBadgeCount: () => void;
  readNotification: (id: string) => void;
};

export const NotificationUserContext = createContext({
  badgeCount: 0,
} as NotificationUserState);

export const NotificationUserProvider: FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [count, setCount] = useState(0);
  const [hasNew, setHasNew] = useState(false);
  const { data, refetch } = useGetNotificationBadgeCountQuery();
  const [readNotification] = useReadNotificationMutation();
  const [resetBadgeCount] = useResetNotificationBadgeCountMutation();

  const onResetBadgeCount = async () => {
    setCount(0);
    setHasNew(false);
    await resetBadgeCount();
  };

  const onReadNotification = async (id: string) => {
    await readNotification(id);
    refetch();
  };

  useEffect(() => {
    setCount((prevCount) => {
      const total = data?.count || 0;
      setHasNew(total > prevCount);
      return total;
    });
  }, [data?.count]);

  useEffect(() => {
    if (!hasNew) {
      return;
    }
    const t = setTimeout(() => {
      setHasNew(false);
    }, 3000);

    return () => clearTimeout(t);
  }, [hasNew]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      refetch();
    }, 5 * 60000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <NotificationUserContext.Provider
      value={{
        badgeCount: count,
        hasNew,
        onResetBadgeCount,
        readNotification: onReadNotification,
      }}
    >
      {children}
    </NotificationUserContext.Provider>
  );
};
